import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/text';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { ItemStatus, OccurrenceItem } from '@/types/admin/todoLists';
import { ItemStatusSelect } from './ItemStatusControl';

interface Props {
  item: OccurrenceItem;
  onChange: (status: ItemStatus) => void;
  /** Resolved display name for `item.completedByAccountId`; undefined when unknown. */
  completedByName?: string;
  disabled?: boolean;
}

/**
 * The "Completed by …" meta text for a done item. Resolution order:
 *   1. The embedded `item.completedBy` — full name, or whichever of first/last
 *      is present when one part is missing.
 *   2. The `completedByName` prop (the staff-map lookup) when 1 yields nothing.
 *   3. Plain 'Completed' when no name is resolvable.
 *
 * Exported as a pure helper so the row's meta rule is unit-testable in isolation.
 */
export function formatCompletedBy(
  item: OccurrenceItem,
  completedByName?: string,
): string {
  const embedded = [item.completedBy?.firstName, item.completedBy?.lastName]
    .filter(Boolean)
    .join(' ');
  const name = embedded || completedByName || '';
  return name ? `Completed by ${name}` : 'Completed';
}

/**
 * A single occurrence checklist row, composed as
 * `[label + meta block, flex] [ItemStatusSelect]`.
 *
 * Rows are UNIFORM height regardless of status: the meta line (line 2) is always
 * rendered with a fixed height, so completing/uncompleting an item never shifts
 * the layout. The meta line shows the completed-by text only when `done`, and an
 * empty string otherwise (occupying identical space via minHeight + lineHeight).
 * A `skipped` item is visually deprioritized by muting its label. The status
 * cluster may wrap as a unit under the label block on narrow widths.
 */
export const OccurrenceItemRow: React.FC<Props> = ({
  item,
  onChange,
  completedByName,
  disabled = false,
}) => {
  const { redesign } = useAdminColors();
  const labelColor =
    item.status === 'skipped' ? redesign.text.muted : redesign.text.primary;
  const metaText =
    item.status === 'done' ? formatCompletedBy(item, completedByName) : '';

  return (
    <View style={styles.row}>
      <View style={styles.labelWrap}>
        <Text style={[styles.label, { color: labelColor }]}>{item.label}</Text>
        <Text
          testID="item-meta"
          style={[styles.meta, { color: redesign.text.muted }]}
        >
          {metaText}
        </Text>
      </View>

      <ItemStatusSelect
        status={item.status}
        onChange={onChange}
        disabled={disabled}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  // flexWrap on the outer row lets the status cluster reflow, as a unit, under
  // the label block on narrow widths; the structure is identical per row so the
  // wrap behavior stays uniform.
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 10,
    flexWrap: 'wrap',
  },
  labelWrap: { flex: 1, gap: 2, minWidth: 160 },
  label: { fontSize: 15, fontWeight: '600' },
  // minHeight + lineHeight reserve a constant line so an empty meta occupies the
  // exact space of a populated one — the row never changes height with status.
  meta: { fontSize: 12, minHeight: 16, lineHeight: 16 },
});
